/**
 * /admin/archive (BUILD_SPEC.md §10.1).
 *
 * Lists ARCHIVED transactions; offers an unarchive action. Read-only
 * besides that — editing notes or other lifecycle changes belong on
 * /admin/transactions.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, FileDown, Download } from "lucide-react";
import { listTransactions, unarchiveTransaction } from "@/api/transactions";
import { downloadArchiveExport, downloadTransactionExport } from "@/api/exports";
import type { Transaction } from "@/api/types";
import { DataTable, type Column } from "@/components/DataTable";

export function ArchiveAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ["transactions", "archive", { page }],
    queryFn: () => listTransactions({ status: "ARCHIVED", page, limit: 50 }),
  });
  const unarchiveMutation = useMutation({
    mutationFn: unarchiveTransaction,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transactions"] }),
  });
  const archiveExportMutation = useMutation({ mutationFn: downloadArchiveExport });
  const txExportMutation = useMutation({
    mutationFn: (id: string) => downloadTransactionExport(id),
  });
  const hasRows = (query.data?.items.length ?? 0) > 0;

  const columns: Column<Transaction>[] = [
    {
      key: "requestNumber",
      header: t("tx.requestNumber"),
      render: (r) => (
        <span className="rtl-isolate font-medium text-fg dark:text-fg-dark">{r.requestNumber}</span>
      ),
    },
    {
      key: "owner",
      header: t("tx.ownerName"),
      render: (r) => <span className="rtl-isolate">{r.ownerName ?? "—"}</span>,
    },
    {
      key: "region",
      header: t("tx.region"),
      render: (r) => <span className="rtl-isolate">{r.region}</span>,
      hideOnMobile: true,
    },
    {
      key: "archivedAt",
      header: t("tx.lastChanged"),
      render: (r) =>
        r.archivedAt ? new Date(r.archivedAt).toLocaleDateString() : "—",
      hideOnMobile: true,
    },
    {
      key: "actions",
      header: t("common.actions"),
      render: (r) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => unarchiveMutation.mutate(r.id)}
            aria-label={t("tx.unarchive")}
            title={t("tx.unarchive")}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            <Archive className="h-4 w-4" />
            {t("tx.unarchive")}
          </button>
          <button
            type="button"
            onClick={() => txExportMutation.mutate(r.id)}
            disabled={txExportMutation.isPending}
            aria-label={t("export.transaction")}
            title={t("export.transaction")}
            className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.limit)) : 1;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-bold text-fg dark:text-fg-dark">{t("admin.archive")}</h2>
        <button
          type="button"
          onClick={() => archiveExportMutation.mutate()}
          disabled={!hasRows || archiveExportMutation.isPending}
          aria-label={t("export.archive")}
          title={hasRows ? t("export.archive") : t("export.noTransactions")}
          className="inline-flex items-center gap-1.5 rounded-md border border-hairline dark:border-hairline-dark px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 disabled:opacity-50"
        >
          <FileDown className="h-4 w-4" aria-hidden />
          {archiveExportMutation.isPending ? t("export.exporting") : t("export.archive")}
        </button>
      </div>
      {archiveExportMutation.error && (
        <p className="rounded-md bg-rejection-bg dark:bg-rejection-dark-bg/40 p-2 text-sm text-rejection dark:text-rejection-dark">
          {(archiveExportMutation.error as Error).message}
        </p>
      )}
      <DataTable
        rows={query.data?.items ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        emptyMessage={t("archive.empty")}
        isLoading={query.isLoading}
      />
    </div>
  );
}
